import { useMemo } from "react";
import { useTheme } from "@mui/material/styles";

import { type TooltipProps } from "ui/tooltip/tooltip.types";

type UseTooltipParams = Pick<TooltipProps, "label" | "disabled" | "suppressed"> & { offset: number };

export const useTooltip = ({ label, disabled, suppressed, offset }: UseTooltipParams) => {
  // MUI's Popper lists `modifiers` in its effect dependencies, so a fresh array on every render
  // destroys and recreates the popper instance while the tooltip is open.
  const popperModifiers = useMemo(() => [{ name: "offset", options: { offset: [0, offset] } }], [offset]);

  const { shape } = useTheme();

  // MUI holds the caret 4px from the surface's end, inside its 6px corner, so on a `-start` / `-end`
  // placement the rounding cuts away under the caret's base. Passed through `popperOptions`, not
  // `modifiers`: MUI concatenates its own arrow modifier after `modifiers`, and the later entry wins.
  const popperOptions = useMemo(
    () => ({ modifiers: [{ name: "arrow", options: { padding: shape.medium } }] }),
    [shape.medium],
  );

  return {
    isInert: Boolean(disabled) || !label,
    // An empty title keeps MUI's Tooltip mounted, and with it the anchor wrapper, but never opens.
    title: suppressed ? "" : label,
    popperModifiers,
    popperOptions,
  };
};
